import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/Button'
import { createMaterialApi, type Material, type MaterialKind } from '@/services/materials'
import { X, AlertCircle } from 'lucide-react'
import { normalizeUomLabel } from '@/lib/uom'
import { MATERIAL_GROUPS, detectMaterialGroup, type MaterialGroupCode } from '@/domain/materialGroups'

interface CreateMaterialModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (material: Material) => void
}

export function CreateMaterialModal({
  isOpen,
  onClose,
  onSuccess,
}: CreateMaterialModalProps) {
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [kind, setKind] = useState<MaterialKind>('RAW')
  const [groupCode, setGroupCode] = useState<MaterialGroupCode>('go')
  const [userSelectedGroup, setUserSelectedGroup] = useState(false)
  const [quyCach, setQuyCach] = useState('')
  const [mau, setMau] = useState('')
  const [uomDefault, setUomDefault] = useState('')
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!userSelectedGroup && name) {
      const suggested = detectMaterialGroup(name, kind)
      setGroupCode(suggested)
    }
  }, [name, kind, userSelectedGroup])

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmedCode = code.trim()
    const trimmedName = name.trim()
    const trimmedUom = uomDefault.trim()

    if (!trimmedName) {
      setError('Vui lòng nhập tên vật tư.')
      return
    }
    if (!trimmedUom) {
      setError('Vui lòng nhập đơn vị gốc (bắt buộc).')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const payload: Record<string, unknown> = {
        name: trimmedName,
        kind,
        group_code: groupCode,
        uom_default: normalizeUomLabel(trimmedUom),
        notes: notes.trim() || undefined,
        status: 'active',
      }
      if (trimmedCode) {
        payload.code = trimmedCode
      }
      if (groupCode === 'nk') {
        const attributes: Record<string, any> = {}
        if (quyCach.trim()) attributes.quy_cach = quyCach.trim()
        if (mau.trim()) attributes.mau = mau.trim()
        payload.attributes = attributes
      }

      const created = await createMaterialApi(payload as any)
      // Reset form
      setCode('')
      setName('')
      setKind('RAW')
      setGroupCode('go')
      setUserSelectedGroup(false)
      setQuyCach('')
      setMau('')
      setUomDefault('')
      setNotes('')
      onSuccess(created)
      onClose()
    } catch (err: unknown) {
      const errObj = err as { code?: string; message?: string }
      if (errObj.code === 'DUPLICATE_CODE') {
        setError(`Mã vật tư "${trimmedCode}" đã tồn tại trên hệ thống.`)
      } else {
        setError(errObj.message || 'Không thể tạo vật tư mới. Vui lòng thử lại.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <div className="mb-4 flex items-center justify-between border-b pb-3">
          <h2 className="text-base font-semibold text-slate-800">
            Tạo vật tư mới (Kho)
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {error ? (
          <div className="mb-4 flex items-center gap-2 rounded border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="space-y-3.5 text-xs">
          <div>
            <label className="mb-1 block font-medium text-slate-700">
              Tên vật tư <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              className="w-full rounded border border-slate-300 px-2.5 py-1.5 focus:border-blue-500 focus:outline-none"
              placeholder="VD: Pát sắt chữ L 50x50"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
              autoFocus
            />
          </div>

          <div>
            <label className="mb-1 block font-medium text-slate-700">
              Mã vật tư <span className="font-normal text-slate-400">(Tùy chọn - để trống hệ thống tự cấp)</span>
            </label>
            <input
              type="text"
              className="w-full rounded border border-slate-300 px-2.5 py-1.5 focus:border-blue-500 focus:outline-none"
              placeholder="VD: PAT-SAT-01 (không bắt buộc)"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="mb-1 block font-medium text-slate-700">
                Nhóm vật tư <span className="text-rose-500">*</span>
              </label>
              <select
                className="w-full rounded border border-slate-300 px-2 py-1.5 focus:border-blue-500 focus:outline-none bg-white text-xs"
                value={groupCode}
                onChange={(e) => {
                  setGroupCode(e.target.value as MaterialGroupCode)
                  setUserSelectedGroup(true)
                }}
                disabled={loading}
              >
                {MATERIAL_GROUPS.map((g) => (
                  <option key={g.code} value={g.code}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block font-medium text-slate-700">
                Loại vật tư <span className="text-rose-500">*</span>
              </label>
              <select
                className="w-full rounded border border-slate-300 px-2 py-1.5 focus:border-blue-500 focus:outline-none bg-white text-xs"
                value={kind}
                onChange={(e) => setKind(e.target.value as MaterialKind)}
                disabled={loading}
              >
                <option value="RAW">Nguyên liệu (RAW)</option>
                <option value="AUX">Phụ liệu (AUX)</option>
              </select>
            </div>

            <div>
              <label className="mb-1 block font-medium text-slate-700">
                Đơn vị gốc (UOM) <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                className="w-full rounded border border-slate-300 px-2 py-1.5 focus:border-blue-500 focus:outline-none text-xs"
                placeholder="VD: m³, Tấm..."
                value={uomDefault}
                onChange={(e) => setUomDefault(e.target.value)}
                onBlur={() => {
                  if (uomDefault) setUomDefault((v) => normalizeUomLabel(v))
                }}
                disabled={loading}
              />
            </div>
          </div>

          {groupCode === 'nk' && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="mb-1 block font-medium text-slate-700">
                  Quy cách
                </label>
                <input
                  type="text"
                  className="w-full rounded border border-slate-300 px-2 py-1.5 focus:border-blue-500 focus:outline-none text-xs"
                  placeholder="VD: M4x20, 13x35x200..."
                  value={quyCach}
                  onChange={(e) => setQuyCach(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div>
                <label className="mb-1 block font-medium text-slate-700">
                  Màu
                </label>
                <input
                  type="text"
                  className="w-full rounded border border-slate-300 px-2 py-1.5 focus:border-blue-500 focus:outline-none text-xs"
                  placeholder="VD: 7 màu, Trắng, Đen..."
                  value={mau}
                  onChange={(e) => setMau(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>
          )}

          <div>
            <label className="mb-1 block font-medium text-slate-700">
              Ghi chú
            </label>
            <textarea
              rows={2}
              className="w-full rounded border border-slate-300 px-2.5 py-1.5 focus:border-blue-500 focus:outline-none"
              placeholder="Ghi chú quy cách, mục đích sử dụng..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="mt-5 flex justify-end gap-2 border-t pt-3">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={loading}
            >
              Hủy
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
            >
              {loading ? 'Đang tạo...' : 'Tạo vật tư'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
